import React, { useEffect, useState } from 'react';
import { Card, Steps, Space, Button, Tag, Descriptions } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { loadState } from './PublishNoticeEditContent/state';
import { getDebtDetail, getDebtNoticeBatchStatus } from '../../services/debt';
import { useBreakpoint } from '../../hooks/useBreakpoint';

/**
 * 催告发布步骤7：归档
 * 说明：展示本次发送批次的归档概要信息，并引导用户前往催告历史等后续操作。
 */
const PublishNoticeArchivePage = () => {
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const { id } = useParams();
  const state = loadState();
  const debtId = id || state.selectedDebtId;
  const [debtDetail, setDebtDetail] = useState(null);
  const [batchStatus, setBatchStatus] = useState(state.noticeBatchStatus || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!debtId || !state.saved) {
      navigate('/debt-notice/publish');
    }
  }, [debtId, navigate, state.saved]);

  useEffect(() => {
    if (!debtId) {
      return;
    }
    getDebtDetail(debtId).then(({ data }) => {
      setDebtDetail(data);
    }).catch(() => {});
  }, [debtId]);

  useEffect(() => {
    const batchId = state.noticeLastBatchId || state.noticeBatchStatus?.batchId;
    if (!batchId) {
      return;
    }
    setLoading(true);
    getDebtNoticeBatchStatus(batchId).then(({ data }) => {
      setBatchStatus(data);
    }).catch(() => {}).finally(() => {
      setLoading(false);
    });
  }, []);

  const goToHistory = () => {
    navigate('/debt-notice/history');
  };

  const goToFollowup = () => {
    if (!debtId) return;
    const sp = new URLSearchParams();
    sp.set('tab', '2');
    sp.set('debtId', String(debtId));
    navigate(`/debt-management/followup?${sp.toString()}`);
  };

  const goToPublishDebt = () => {
    navigate('/debt-management/publish');
  };

  const steps = [
    { title: '选择债权' },
    { title: '选择模板' },
    { title: '编辑内容' },
    { title: '签章与预览' },
    { title: '生成催告函' },
    { title: '发送催告' },
    { title: '归档' },
  ];
  const currentStep = 6;

  return (
    <div className="page-container">
      <div className="page-content page-content-wide">
        <h1 className="page-title">发布催告</h1>

        <Card className="card-compact" size="small">
          {isMobile ? (
            <div className="c-steps-mobile-header" aria-label="步骤信息">
              <div className="c-steps-mobile-left">
                <span className="c-steps-mobile-stepno">{`第 ${currentStep + 1} 步`}</span>
                <span className="c-steps-mobile-title">{steps[currentStep]?.title || ''}</span>
              </div>
              <div className="c-steps-mobile-progress">{`${currentStep + 1}/${steps.length}`}</div>
            </div>
          ) : (
            <Steps current={currentStep} size="small" items={steps} />
          )}
        </Card>

        <Card>
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <div style={{ color: '#666' }}>
              本次催告已完成发送并自动归档，您可以在「催告历史」中查看详细记录与下载文件。
            </div>

            <Space
              direction={isMobile ? 'vertical' : 'horizontal'}
              wrap={!isMobile}
              style={{ width: isMobile ? '100%' : undefined, marginBottom: 8 }}
            >
              <Button type="primary" onClick={goToHistory} block={isMobile}>
                催告历史及重发
              </Button>
              <Button onClick={goToFollowup} disabled={!debtId} block={isMobile}>
                新增跟进日志
              </Button>
              <Button onClick={goToPublishDebt} block={isMobile}>
                发布债权
              </Button>
            </Space>

            <Descriptions
              title="债权概要"
              size="small"
              column={isMobile ? 1 : 2}
              bordered
            >
              <Descriptions.Item label="债权名称">
                {debtDetail?.debt?.title || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="债权编号">
                {debtDetail?.debt?.debtNo || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="债权类型">
                {debtDetail?.debt?.debtType || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="发生日">
                {debtDetail?.debt?.debtOccurrenceDate ? dayjs(debtDetail.debt.debtOccurrenceDate).format('YYYY-MM-DD') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="合计金额">
                {debtDetail?.debt?.totalAmount != null ? debtDetail.debt.totalAmount : '-'}
              </Descriptions.Item>
            </Descriptions>

            <Card
              size="small"
              title="发送批次概要"
              loading={loading}
            >
              {batchStatus?.batchId ? (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  <Space wrap>
                    <Tag color="blue">{`批次ID ${batchStatus.batchId}`}</Tag>
                    <Tag color={
                      batchStatus.status === 'SUCCESS'
                        ? 'green'
                        : (batchStatus.status === 'FAILED' ? 'red' : 'orange')
                    }
                    >
                      {batchStatus.status || '-'}
                    </Tag>
                  </Space>
                  <div style={{ color: '#666' }}>
                    <span style={{ marginRight: 16 }}>
                      发送时间：
                      {batchStatus.sentAt ? dayjs(batchStatus.sentAt).format('YYYY-MM-DD HH:mm') : '-'}
                    </span>
                    <span>
                      完成时间：
                      {batchStatus.finishedAt ? dayjs(batchStatus.finishedAt).format('YYYY-MM-DD HH:mm') : '-'}
                    </span>
                  </div>
                  <div style={{ color: '#666' }}>
                    收件人：
                    {batchStatus.stats?.recipientSuccess || 0}
                    {' 个成功，'}
                    {batchStatus.stats?.recipientFailed || 0}
                    {' 个失败，共 '}
                    {batchStatus.stats?.recipientTotal || 0}
                    {' 个'}
                  </div>
                  <div style={{ color: '#666' }}>
                    发送任务：
                    {batchStatus.stats?.taskSuccess || 0}
                    {' 条成功，'}
                    {batchStatus.stats?.taskFailed || 0}
                    {' 条失败（待执行 '}
                    {batchStatus.stats?.taskPending || 0}
                    {' 条，执行中 '}
                    {batchStatus.stats?.taskRunning || 0}
                    {' 条）'}
                  </div>
                </Space>
              ) : (
                <div style={{ color: '#666' }}>
                  未找到发送批次信息，请确认已在上一步完成「确认发送」操作。
                </div>
              )}
            </Card>
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default PublishNoticeArchivePage;
